import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Clock, Eye } from "lucide-react";
import { FeedbackResultDoc } from "@/dao/firestore/FeedbackResultsRepository";
import { PersonaDoc } from "@/dao/firestore/PersonasRepository";
import { useTranslation } from "react-i18next";

interface PersonalFeedbackResultProps {
  result: FeedbackResultDoc;
  persona?: PersonaDoc;
  onClick: () => void;
}

export function PersonalFeedbackResult({ result, persona, onClick }: PersonalFeedbackResultProps) {
  const { t } = useTranslation();
  
  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={persona?.image || '/placeholder.svg'} alt={result.personaName} />
            <AvatarFallback>{result.personaName?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">{result.personaName}</h4>
                <p className="text-xs text-muted-foreground">{t('studio.critics.inTheStyleOf')}</p>
              </div>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500 fill-current" />
                <span className="text-sm font-medium">{result.rating || 0}/10</span>
              </div>
            </div>
            
            {result.comment && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {result.comment}
              </p>
            )}
            
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {result.better_moment && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{t('studio.critics.betterMoment')}: {formatTime(result.better_moment.millisecond || 0)}</span>
                </div>
              )}
              {result.visual && (
                <Badge variant="outline" className="text-xs">
                  {t('studio.critics.visual')}: {result.visual.rating}/10
                </Badge>
              )}
              {result.content && (
                <Badge variant="outline" className="text-xs">
                  {t('studio.critics.content')}: {result.content.rating}/10
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}